/**
 * F-148 §4.7 scan capture queue (AC31).
 *
 * Every submit from the barcode input becomes one capture with a fresh `captureId`. Captures are
 * queued FIFO and looked up one at a time, so results are applied in scan order. The lookup is
 * injected (the unchanged F045 `findVariantByBarcode` contract); a thrown lookup becomes
 * `lookup_failed` so the value stays in the input for a retry.
 *
 * Camera-sourced values identical to the previous camera value within
 * `RECEIVING_LIMITS.scanCameraRepeatWindowMs` are dropped before queueing (defensive dedupe on
 * top of the dialog's one-detection-per-session guard). Keyboard/wedge entries never dedupe.
 */
import type { BarcodeLookupResult } from '@dukanti/shared';
import { shouldIgnoreCameraRepeat, type ReceivingScanLookup } from '@dukanti/shared/admin';

export type ScanSource = 'keyboard' | 'camera';

export interface ScanCapture {
  captureId: string;
  rawBarcode: string;
  source: ScanSource;
}

export interface ScanQueueOptions {
  lookup: (rawBarcode: string, capture: ScanCapture) => Promise<BarcodeLookupResult>;
  onResult: (capture: ScanCapture, lookup: ReceivingScanLookup) => void;
  /** Called when the queue transitions between idle and busy. */
  onPendingChange?: (pending: boolean) => void;
  createCaptureId?: () => string;
  now?: () => number;
  cameraRepeatWindowMs?: number;
}

export type EnqueueResult =
  | { queued: true; capture: ScanCapture }
  | { queued: false; reason: 'camera_repeat' };

export interface ScanQueue {
  enqueue: (rawBarcode: string, source: ScanSource) => EnqueueResult;
  /** Number of captures waiting or in flight. */
  size: () => number;
  /** Drops queued captures and ignores the in-flight result (component unmount). */
  dispose: () => void;
}

function defaultCaptureId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createScanQueue(options: ScanQueueOptions): ScanQueue {
  const createCaptureId = options.createCaptureId ?? defaultCaptureId;
  const now = options.now ?? (() => Date.now());
  const queue: ScanCapture[] = [];
  let inFlight = false;
  let disposed = false;
  let lastCamera: { value: string; at: number } | null = null;

  const setPending = (pending: boolean): void => {
    options.onPendingChange?.(pending);
  };

  const pump = (): void => {
    if (disposed || inFlight) return;
    const next = queue.shift();
    if (!next) {
      setPending(false);
      return;
    }
    inFlight = true;
    setPending(true);
    void options
      .lookup(next.rawBarcode, next)
      .then(
        (result): ReceivingScanLookup => result,
        (): ReceivingScanLookup => ({ status: 'lookup_failed' }),
      )
      .then((lookup) => {
        inFlight = false;
        if (disposed) return;
        options.onResult(next, lookup);
        pump();
      });
  };

  return {
    enqueue: (rawBarcode, source) => {
      if (source === 'camera') {
        const at = now();
        const candidate = { value: rawBarcode, at };
        if (shouldIgnoreCameraRepeat(lastCamera, candidate, options.cameraRepeatWindowMs)) {
          return { queued: false, reason: 'camera_repeat' };
        }
        lastCamera = candidate;
      }
      const capture: ScanCapture = { captureId: createCaptureId(), rawBarcode, source };
      queue.push(capture);
      pump();
      return { queued: true, capture };
    },
    size: () => queue.length + (inFlight ? 1 : 0),
    dispose: () => {
      disposed = true;
      queue.length = 0;
    },
  };
}
